import { useEffect, useState } from 'react'
import { PeriodService } from '../../services/PeriodService'
import { Period } from '../../types/definitions/Period'
import { SortDirection, SortSpec } from '../molecules/dashboard/sortableHeader'

export const parsePageParam = (raw: string | null): number => {
  if (!raw) return 0
  const n = Number(raw)
  return Number.isInteger(n) && n >= 0 ? n : 0
}

export const parseSortParam = <TField extends string>(
  raw: string | null,
  allowedFields: readonly TField[]
): SortSpec<TField> | undefined => {
  if (!raw) return undefined
  const [field, direction] = raw.split(',')
  if (!allowedFields.includes(field as TField)) return undefined
  if (direction !== 'asc' && direction !== 'desc') return undefined
  return raw as SortSpec<TField>
}

export type UrlUpdates = Record<string, string | null>

export const buildUpdateUrl =
  (locationQuery: URLSearchParams, replace: (search: string) => void) =>
  (updates: UrlUpdates): void => {
    const params = new URLSearchParams(locationQuery.toString())
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') params.delete(key)
      else params.set(key, value)
    })
    replace(params.toString())
  }

type ReviewsQueryHandlers<TSort extends string> = {
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onSearchChange: (value: string) => void
  onReviewerNameChange: (value: string) => void
}

type AdminHandlers<TSort extends string> = {
  handlePageChange: (page: number) => void
  handlePageSizeChange: (size: number) => void
  handlePeriodChange: (newPeriodId: number | undefined) => void
  handleSearchChange: (value: string) => void
  handleReviewerNameChange: (value: string) => void
  handleSortChange: (sort: TSort | undefined) => void
  handleClearReviewer: () => void
}

export const buildAdminReviewsHandlers = <TSort extends string>(
  query: ReviewsQueryHandlers<TSort>,
  updateUrl: (updates: UrlUpdates) => void
): AdminHandlers<TSort> => ({
  handlePageChange: page => {
    query.onPageChange(page)
    updateUrl({ page: String(page) })
  },
  handlePageSizeChange: size => {
    query.onPageSizeChange(size)
    updateUrl({ page: '0' })
  },
  handlePeriodChange: newPeriodId => {
    updateUrl({ periodId: newPeriodId === undefined ? null : String(newPeriodId), page: '0' })
  },
  handleSearchChange: value => {
    query.onSearchChange(value)
    updateUrl({ search: value || null, page: '0' })
  },
  handleReviewerNameChange: value => {
    query.onReviewerNameChange(value)
    updateUrl({ reviewerName: value || null, page: '0' })
  },
  handleSortChange: sort => {
    updateUrl({ sort: sort ?? null, page: '0' })
  },
  handleClearReviewer: () => {
    updateUrl({ reviewerId: null, page: '0' })
  },
})

export const usePeriods = (): Period[] => {
  const [periods, setPeriods] = useState<Period[]>([])
  useEffect(() => {
    PeriodService.findAll()
      .then(setPeriods)
      .catch(() => undefined)
  }, [])
  return periods
}

// Re-export so admin pages don't need to dig into the molecules folder for the type.
export type { SortDirection, SortSpec }
